import { useEffect, useRef } from "react";

const DEFAULT_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
];

const ACTIVITY_KEY = "autoLogout_lastActivity";
const LOGOUT_KEY = "autoLogout_loggedOut";

export default function useAutoLogout({
  timeout = 15 * 60 * 1000, // 15 minutes
  onLogout,
}) {
  const timerRef = useRef(null);
  const onLogoutRef = useRef(onLogout);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  const triggerLogout = () => {
    sessionStorage.setItem('loggedOutDueToInactivity', 'true');
    // Write a logout signal so all other tabs pick it up immediately via the storage event
    localStorage.setItem(LOGOUT_KEY, Date.now().toString());
    onLogoutRef.current();
  };

  const scheduleCheck = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const lastActivity = parseInt(localStorage.getItem(ACTIVITY_KEY) || Date.now(), 10);
    const elapsed = Date.now() - lastActivity;
    const remaining = timeout - elapsed;

    if (remaining <= 0) {
      triggerLogout();
    } else {
      timerRef.current = setTimeout(scheduleCheck, remaining);
    }
  };

  const onActivity = () => {
    localStorage.setItem(ACTIVITY_KEY, Date.now().toString());
    scheduleCheck();
  };

  useEffect(() => {
    // Initialise the shared timestamp on first mount if not already set
    if (!localStorage.getItem(ACTIVITY_KEY)) {
      localStorage.setItem(ACTIVITY_KEY, Date.now().toString());
    }

    scheduleCheck();

    DEFAULT_EVENTS.forEach((event) =>
      window.addEventListener(event, onActivity)
    );

    const handleStorageChange = (e) => {
      // Another tab had activity — reschedule our check
      if (e.key === ACTIVITY_KEY) {
        scheduleCheck();
      }
      // Another tab logged out — log this tab out immediately
      if (e.key === LOGOUT_KEY) {
        if (timerRef.current) clearTimeout(timerRef.current);
        sessionStorage.setItem('loggedOutDueToInactivity', 'true');
        onLogoutRef.current();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      DEFAULT_EVENTS.forEach((event) =>
        window.removeEventListener(event, onActivity)
      );
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
}
